"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
      opacityDir: number;
    }> = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
        opacityDir: Math.random() > 0.5 ? 1 : -1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.opacity += p.opacityDir * 0.003;
        if (p.opacity >= 0.6 || p.opacity <= 0.05) p.opacityDir *= -1;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 230, 118, ${p.opacity})`;
        ctx.fill();
      });

      // Draw faint connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 230, 118, ${0.06 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0B0F14]"
    >
      {/* Animated Grid */}
      <div className="animated-grid absolute inset-0 z-0" />

      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full z-0 pointer-events-none"
        aria-hidden="true"
      />

      {/* Radial Glow Center */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(0,230,118,0.06) 0%, transparent 70%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-32 pb-24">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 tag-badge mb-8">
          <span className="status-dot w-1.5 h-1.5 rounded-full bg-[#00E676] block" />
          Infrastructure OS · v2.6 · Onboarding Open
        </div>

        {/* Main Headline */}
        <h1 className="hero-title text-[#F0F4F8] mb-6 max-w-4xl mx-auto">
          Built For Advertisers{" "}
          <span
            className="text-[#00E676]"
            style={{ textShadow: "0 0 40px rgba(0,230,118,0.4)" }}
          >
            Who Refuse Limits.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-[#6B7A8D] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10 font-light">
          Meta-focused advertising infrastructure engineered for unrestricted
          scale. No spending limits. No monthly fees. 24/7 capital deployment.
        </p>

        {/* CTA Group */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
          <Link
            href="/contact"
            className="btn-solid-glow px-10 py-4 rounded-full text-sm font-bold uppercase tracking-[0.1em] inline-block"
          >
            Launch Infrastructure →
          </Link>
          <Link
            href="/infrastructure"
            className="btn-glow px-10 py-4 rounded-full text-sm font-bold uppercase tracking-[0.1em] inline-block"
          >
            View Architecture
          </Link>
        </div>

        {/* Microtext */}
        <p className="text-[#6B7A8D] text-xs font-mono tracking-[0.08em]">
          Onboarding review required before activation.
        </p>

        {/* Bottom metrics strip */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(0,230,118,0.08)] rounded-2xl overflow-hidden max-w-3xl mx-auto">
          {[
            { val: "$25M+", label: "Monthly Ad Volume" },
            { val: "1,000+", label: "Active Deployments" },
            { val: "99.2%", label: "Infrastructure Uptime" },
            { val: "24/7", label: "Operational Coverage" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-[#11161D] px-6 py-5 text-center"
            >
              <div className="text-[#00E676] font-mono font-bold text-xl mb-1">
                {stat.val}
              </div>
              <div className="text-[#6B7A8D] text-[10px] uppercase tracking-[0.12em] font-bold">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}